import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import { NODE_TYPES, WIDGETS, SCHEMA_BUILDER_VERSION } from './constants';

export const ROOT_ID = '__root__';

let _counter = 0;
export const genId = () =>
  `n_${Date.now().toString(36)}_${(_counter += 1).toString(36)}`;

export const makeNode = (overrides = {}) => ({
  id: overrides.id || genId(),
  parentId: null,
  name: '',
  nodeType: NODE_TYPES.PRIMITIVE,
  primitiveType: 'string',
  widget: WIDGETS.INPUT,
  titleKey: '',
  lookupKey: '',
  lookupParams: [],
  enumValues: [],
  maxLength: null,
  min: null,
  max: null,
  constantValue: '',
  defaultValue: null, // <-- AGGIUNTO DEFAULT VALUE
  required: false,
  childrenIds: [],
  ...overrides,
});

export const nodeAtomFamily = atomFamily((id) => atom(makeNode({ id })));
export const allNodeIdsAtom = atom([ROOT_ID]);
export const selectedNodeIdAtom = atom(ROOT_ID);

export const addChildAtom = atom(null, (get, set, { parentId, kind }) => {
  const id = genId();
  const parent = get(nodeAtomFamily(parentId));

  const initialWidget = WIDGETS.INPUT;

  set(
    nodeAtomFamily(id),
    makeNode({
      id,
      parentId,
      name: defaultNameFor(kind, parent.childrenIds.length),
      nodeType: kind,
      primitiveType: 'string',
      widget: initialWidget,
    })
  );
  set(nodeAtomFamily(parentId), {
    ...parent,
    childrenIds: [...parent.childrenIds, id],
  });
  set(allNodeIdsAtom, (prev) => [...prev, id]);
  set(selectedNodeIdAtom, id);
});

export const removeNodeAtom = atom(null, (get, set, id) => {
  if (id === ROOT_ID) return;
  const node = get(nodeAtomFamily(id));
  const collectDescendants = (nid, acc = []) => {
    const n = get(nodeAtomFamily(nid));
    n.childrenIds.forEach((c) => collectDescendants(c, acc));
    acc.push(nid);
    return acc;
  };
  const toRemove = collectDescendants(id);
  const parent = get(nodeAtomFamily(node.parentId));
  set(nodeAtomFamily(node.parentId), {
    ...parent,
    childrenIds: parent.childrenIds.filter((c) => c !== id),
  });
  toRemove.forEach((rid) => nodeAtomFamily.remove(rid));
  set(allNodeIdsAtom, (prev) => prev.filter((nid) => !toRemove.includes(nid)));
  if (toRemove.includes(get(selectedNodeIdAtom)))
    set(selectedNodeIdAtom, ROOT_ID);
});

export const moveChildAtom = atom(
  null,
  (get, set, { parentId, fromIndex, toIndex }) => {
    if (fromIndex === toIndex) return;
    const parent = get(nodeAtomFamily(parentId));
    const next = [...parent.childrenIds];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    set(nodeAtomFamily(parentId), { ...parent, childrenIds: next });
  }
);

export const schemaJsonAtom = atom((get) => {
  const buildNode = (id) => {
    const n = get(nodeAtomFamily(id));

    let uiType = n.primitiveType || 'string';
    if (uiType === 'string') uiType = 'text';
    if (uiType === 'integer' || uiType === 'number') uiType = 'number';

    if (n.nodeType === NODE_TYPES.PRIMITIVE) {
      if (n.widget === WIDGETS.SELECT || n.widget === WIDGETS.DYNAMIC_SELECT)
        uiType = 'select';
      if (n.widget === 'constant') uiType = 'constant';
    } else if (n.nodeType === NODE_TYPES.ARRAY) {
      if (n.widget === 'constant') {
        uiType = 'constant';
      } else {
        uiType = n.widget === WIDGETS.INPUT ? 'array' : 'staticArray';
      }
    } else if (n.nodeType === NODE_TYPES.OBJECT) {
      uiType = 'object';
    }

    const fieldConfig = {
      key: n.name,
      label: n.titleKey || n.name,
      type: uiType,
      required: !!n.required,
    };

    if (n.nodeType === NODE_TYPES.PRIMITIVE) {
      if (n.widget === 'constant') {
        if (n.primitiveType === 'boolean') {
          fieldConfig.value =
            n.constantValue === true || n.constantValue === 'true';
        } else if (
          n.primitiveType === 'number' ||
          n.primitiveType === 'integer'
        ) {
          fieldConfig.value =
            n.constantValue === '' ? null : Number(n.constantValue);
        } else {
          fieldConfig.value = String(n.constantValue || '');
        }
      } else {
        // --- LOGICA ESPORTAZIONE DEFAULT VALUE ---
        if (n.defaultValue !== null && n.defaultValue !== '') {
          if (n.primitiveType === 'boolean') {
            fieldConfig.defaultValue =
              n.defaultValue === true || n.defaultValue === 'true';
          } else if (
            n.primitiveType === 'number' ||
            n.primitiveType === 'integer'
          ) {
            fieldConfig.defaultValue = Number(n.defaultValue);
          } else {
            fieldConfig.defaultValue = String(n.defaultValue);
          }
        }

        if (n.widget === WIDGETS.SELECT) {
          fieldConfig.options = n.enumValues.map((e) => ({
            label: e.label || e.value,
            value: e.value,
          }));
        } else if (n.widget === WIDGETS.DYNAMIC_SELECT) {
          fieldConfig.optionsUrl = n.lookupKey;
        }

        if (uiType === 'text' && n.maxLength != null && n.maxLength !== '') {
          fieldConfig.maxLength = Number(n.maxLength);
        }
        if (uiType === 'number') {
          if (n.min != null && n.min !== '') fieldConfig.min = Number(n.min);
          if (n.max != null && n.max !== '') fieldConfig.max = Number(n.max);
        }
      }
    } else if (
      n.nodeType === NODE_TYPES.ARRAY ||
      n.nodeType === NODE_TYPES.OBJECT
    ) {
      if (n.nodeType === NODE_TYPES.ARRAY) {
        if (n.widget === 'constant') {
          fieldConfig.value = n.enumValues
            .map((e) => e.value)
            .filter((v) => v !== '');
        } else {
          if (n.widget === WIDGETS.DYNAMIC_SELECT) {
            fieldConfig.optionsUrl = n.lookupKey;
          } else if (n.widget === WIDGETS.SELECT) {
            fieldConfig.options = n.enumValues.map((e) => ({
              label: e.label || e.value,
              value: e.value,
            }));
          }
        }
      }

      if (n.widget !== 'constant') {
        fieldConfig.itemSchema = n.childrenIds.map((cid) => buildNode(cid));
      }
    }

    return fieldConfig;
  };

  const root = get(nodeAtomFamily(ROOT_ID));
  return root.childrenIds.map((cid) => buildNode(cid));
});

export const validationAtom = atom((get) => {
  const errors = [];
  const visit = (id) => {
    const n = get(nodeAtomFamily(id));
    if (id !== ROOT_ID && !n.name)
      errors.push({ nodeId: id, message: 'EMPTY_PROPERTY_NAME' });
    if (
      n.nodeType === NODE_TYPES.PRIMITIVE ||
      n.nodeType === NODE_TYPES.ARRAY
    ) {
      if (n.widget === WIDGETS.DYNAMIC_SELECT && !n.lookupKey)
        errors.push({
          nodeId: id,
          message: 'DYNAMIC_SELECT_REQUIRES_LOOKUP_KEY',
        });
      if (n.widget === WIDGETS.SELECT && n.enumValues.length === 0)
        errors.push({
          nodeId: id,
          message: 'SELECT_REQUIRES_AT_LEAST_ONE_OPTION',
        });
    }
    n.childrenIds.forEach(visit);
  };
  visit(ROOT_ID);
  return { errors, isValid: errors.length === 0 };
});

const ALLOWED_KEYS = new Set([
  'key',
  'label',
  'type',
  'required',
  'options',
  'optionsUrl',
  'itemSchema',
  'maxLength',
  'min',
  'max',
  'value',
  'defaultValue', // <-- ABILITIAMO L'IMPORT DEL DEFAULT VALUE
]);

const sanitize = (obj) => {
  if (obj == null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(sanitize);
  const out = {};
  Object.keys(obj).forEach((k) => {
    if (ALLOWED_KEYS.has(k)) out[k] = sanitize(obj[k]);
  });
  return out;
};

export const resetSchemaAtom = atom(null, (get, set) => {
  get(allNodeIdsAtom).forEach((id) => {
    if (id !== ROOT_ID) nodeAtomFamily.remove(id);
  });
  set(
    nodeAtomFamily(ROOT_ID),
    makeNode({ id: ROOT_ID, nodeType: NODE_TYPES.OBJECT })
  );
  set(allNodeIdsAtom, [ROOT_ID]);
  set(selectedNodeIdAtom, ROOT_ID);
});

export const importSchemaAtom = atom(null, (get, set, jsonString) => {
  try {
    const parsed = JSON.parse(jsonString);
    if (!Array.isArray(parsed))
      throw new Error('Expected layout format to be an Array');
    const clean = sanitize(parsed);

    get(allNodeIdsAtom).forEach((id) => {
      if (id !== ROOT_ID) nodeAtomFamily.remove(id);
    });
    const rootNode = makeNode({ id: ROOT_ID, nodeType: NODE_TYPES.OBJECT });
    set(nodeAtomFamily(ROOT_ID), rootNode);
    const ids = [ROOT_ID];

    const hydrateList = (parentId, fieldList) => {
      if (!Array.isArray(fieldList)) return [];
      return fieldList.map((field) => {
        const id = genId();
        ids.push(id);

        let nodeType = NODE_TYPES.PRIMITIVE;
        let primitiveType = 'string';
        let widget = WIDGETS.INPUT;
        let lookupKey = '';
        let enumValues = [];
        let constantValue = '';

        if (field.type === 'constant') {
          widget = 'constant';

          if (Array.isArray(field.value)) {
            nodeType = NODE_TYPES.ARRAY;
            primitiveType = 'array';
            enumValues = field.value.map((val) => ({
              value: String(val),
              label: String(val),
            }));
          } else {
            nodeType = NODE_TYPES.PRIMITIVE;
            if (typeof field.value === 'boolean') primitiveType = 'boolean';
            else if (typeof field.value === 'number') primitiveType = 'number';
            else primitiveType = 'string';
            constantValue = String(field.value ?? '');
          }
        } else if (field.type === 'array' || field.type === 'staticArray') {
          nodeType = NODE_TYPES.ARRAY;
          if (field.type === 'staticArray') {
            if (field.optionsUrl) {
              lookupKey = field.optionsUrl;
              widget = WIDGETS.DYNAMIC_SELECT;
            } else {
              enumValues = (field.options || []).map((o) => ({
                value: o.value,
                label: o.label || '',
              }));
              widget = WIDGETS.SELECT;
            }
          } else {
            widget = WIDGETS.INPUT;
          }
        } else if (field.type === 'object') {
          nodeType = NODE_TYPES.OBJECT;
        } else {
          nodeType = NODE_TYPES.PRIMITIVE;
          if (field.type === 'number') primitiveType = 'number';
          else if (field.type === 'boolean') primitiveType = 'boolean';
          else primitiveType = 'string';

          if (field.options) {
            widget = WIDGETS.SELECT;
            enumValues = field.options.map((o) => ({
              value: o.value,
              label: o.label || '',
            }));
          } else if (field.optionsUrl) {
            widget = WIDGETS.DYNAMIC_SELECT;
            lookupKey = field.optionsUrl;
          }
        }

        const node = makeNode({
          id,
          parentId,
          name: field.key || '',
          titleKey: field.label || '',
          nodeType,
          primitiveType,
          widget,
          lookupKey,
          enumValues,
          maxLength: field.maxLength ?? null,
          min: field.min ?? null,
          max: field.max ?? null,
          constantValue,
          defaultValue: field.defaultValue ?? null, // <-- IDRATA IL DEFAULT VALUE
          required: !!field.required,
        });
        set(nodeAtomFamily(id), node);

        if (
          field.itemSchema &&
          (nodeType === NODE_TYPES.ARRAY || nodeType === NODE_TYPES.OBJECT) &&
          widget !== 'constant'
        ) {
          const childIds = hydrateList(id, field.itemSchema);
          set(nodeAtomFamily(id), { ...node, childrenIds: childIds });
        }
        return id;
      });
    };

    const rootChildIds = hydrateList(ROOT_ID, clean);
    set(nodeAtomFamily(ROOT_ID), { ...rootNode, childrenIds: rootChildIds });
    set(allNodeIdsAtom, ids);
    set(selectedNodeIdAtom, ROOT_ID);
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err.message };
  }
});

function defaultNameFor(kind, index) {
  const prefix =
    kind === NODE_TYPES.ARRAY
      ? 'NewArray'
      : kind === NODE_TYPES.OBJECT
        ? 'NewObject'
        : 'NewField';
  return `${prefix}${index + 1}`;
}

export const SCHEMA_VERSION = SCHEMA_BUILDER_VERSION;
